import { isAxiosError } from 'axios'
import { notifyNativeResolveError, type NativeResolveErrorCode } from './nativeMessaging'
import {
  getLocalizedResolveErrorMessage,
  isResolveErrorCode,
  mapResolveErrorCodeToNative,
  type ResolveErrorCode,
} from './resolveErrors'
import { Strings } from '../../constants/Strings'

type ResolveApiErrorResponse = {
  code?: string
  message?: string
}

export type ResolveErrorClassification = {
  resolveCode?: ResolveErrorCode
  nativeCode?: NativeResolveErrorCode
  message?: string
  context?: Record<string, unknown>
}

export type ResolveErrorHandlingResult = {
  message: string
  classification: ResolveErrorClassification
}

// Codes whose message is the entire user-facing copy — rendered as-is, without
// the generic "Auth error" heading. The lease codes deliberately stay out:
// a blocked lease is treated as an auth error and keeps the standard heading.
const SELF_EXPLANATORY_CODES: readonly ResolveErrorCode[] = ['BUILDING_PENDING_APPROVAL']

export const isSelfExplanatoryError = (classification: ResolveErrorClassification): boolean =>
  !!classification.resolveCode && SELF_EXPLANATORY_CODES.includes(classification.resolveCode)

export const classifyResolveUserError = (error: unknown): ResolveErrorClassification => {
  if (isAxiosError<ResolveApiErrorResponse>(error)) {
    const status = error.response?.status
    const data = error.response?.data
    const message = data?.message ?? (error as Error)?.message
    const context = {
      status,
      axiosCode: error.code,
      url: error.config?.url,
    }

    if (data?.code && isResolveErrorCode(data.code)) {
      return {
        resolveCode: data.code,
        // Prefer our translated copy; the server message is English-only.
        message: getLocalizedResolveErrorMessage(data.code) ?? message,
        context: { ...context, resolveErrorCode: data.code },
      }
    }

    if (!status) {
      return {
        nativeCode: 'SERVER_DOWN',
        message: Strings.get().sessionErrors.networkError,
        context: { ...context, reason: 'network_error' },
      }
    }

    if (status === 401) {
      return { nativeCode: 'AUTH_FAILED', message, context }
    }

    if (status === 403 || status === 404) {
      return { nativeCode: 'INVALID_ACCESS', message, context }
    }

    if (status >= 500) {
      return { nativeCode: 'SERVER_DOWN', message, context }
    }

    return { nativeCode: 'AUTH_FAILED', message, context }
  }

  return {
    nativeCode: 'SERVER_DOWN',
    message: (error as Error)?.message ?? 'Unknown resolve error',
    context: { reason: 'unexpected_error' },
  }
}

export const handleResolveUserError = (error: unknown): ResolveErrorHandlingResult => {
  const classification = classifyResolveUserError(error)
  const nativeCode =
    classification.resolveCode ? mapResolveErrorCodeToNative(classification.resolveCode) : classification.nativeCode

  // Codes we translate carry copy that explains the block (e.g. an ended lease);
  // hand it to the host so it doesn't fall back to the generic native message.
  const nativeMessage = classification.resolveCode
    ? getLocalizedResolveErrorMessage(classification.resolveCode)
    : undefined

  notifyNativeResolveError(nativeCode ?? 'SERVER_DOWN', nativeMessage)

  return {
    message: classification.message ?? (error as Error)?.message ?? 'Failed to resolve user',
    classification,
  }
}

